import React from 'react';

/**
 * BIBLIOTHÈQUE DE SCHÉMAS DE L'AIDE GUIDÉE.
 *
 * Les dessins vivent dans le code, la composition vit en base : un chapitre ne stocke que la CLÉ du
 * schéma qu'il affiche. Emeni choisit dans une liste à vignettes et écrit sa légende ; il ne dessine
 * pas. Quand il lui en faut un nouveau, il le demande et on l'ajoute ici.
 *
 * Deux raisons de ne pas les stocker en base, décidées au § 5 du chantier :
 *  - LE THÈME. Un SVG écrit avec les jetons (`var(--ink)`, `var(--border)`) suit le clair et le sombre.
 *    Une image collée reste figée sur un fond blanc.
 *  - LA SÉCURITÉ. Un SVG libre saisi dans un champ puis rendu en `dangerouslySetInnerHTML`, c'est une
 *    porte ouverte à l'injection de script. On ne l'ouvre pas pour un confort d'édition.
 *
 * Chaque schéma se dessine dans un viewBox de 600 × 200 et s'étire à la largeur disponible.
 */

const ENCRE = 'var(--text-primary, #1f2937)';
const ESTOMPE = 'var(--text-muted, #6b7280)';
const BORDURE = 'var(--border-color, #d8dde3)';
// Le fond d'une boite : `--bg-card`, le jeton de surface de l'application. Ecrit `--bg-surface` au
// premier jet, il n'existait pas : le repli blanc s'appliquait, et en theme sombre les boites
// sortaient blanches avec un texte clair par-dessus, illisibles. Vu a l'ecran avant de livrer.
const FOND = 'var(--bg-card, #ffffff)';
const ACCENT = 'var(--acc, #987b55)';

/** Une boîte avec son intitulé — la brique de tous les schémas. */
const Boite = ({ x, y, w, h, titre, sousTitre, accentuee }: {
    x: number; y: number; w: number; h: number; titre: string; sousTitre?: string; accentuee?: boolean;
}) => (
    <g>
        <rect x={x} y={y} width={w} height={h} rx={10}
            fill={accentuee ? ACCENT : FOND} stroke={accentuee ? ACCENT : BORDURE} strokeWidth={1.5} />
        <text x={x + w / 2} y={sousTitre ? y + h / 2 - 3 : y + h / 2 + 4} textAnchor="middle"
            fontSize={12.5} fontWeight={700} fill={accentuee ? '#fff' : ENCRE}>{titre}</text>
        {sousTitre && (
            <text x={x + w / 2} y={y + h / 2 + 14} textAnchor="middle"
                fontSize={10.5} fill={accentuee ? 'rgba(255,255,255,.85)' : ESTOMPE}>{sousTitre}</text>
        )}
    </g>
);

/** Une flèche horizontale entre deux boîtes. */
const Fleche = ({ x1, x2, y }: { x1: number; x2: number; y: number }) => (
    <g>
        <line x1={x1} y1={y} x2={x2 - 7} y2={y} stroke={ESTOMPE} strokeWidth={1.5} />
        <polygon points={`${x2},${y} ${x2 - 8},${y - 4.5} ${x2 - 8},${y + 4.5}`} fill={ESTOMPE} />
    </g>
);

const Cadre = ({ children }: { children: React.ReactNode }) => (
    <svg viewBox="0 0 600 200" className="w-full h-auto" role="img">{children}</svg>
);

/** Planning : les réglages remplissent la semaine, on ne touche qu'aux exceptions. */
const PlanningSemaineParDefaut = () => (
    <Cadre>
        <Boite x={14} y={62} w={150} h={76} titre="Vos réglages" sousTitre="horaires + repos" accentuee />
        <Fleche x1={170} x2={222} y={100} />
        <Boite x={228} y={40} w={150} h={40} titre="Semaine 38" sousTitre="déjà remplie" />
        <Boite x={228} y={90} w={150} h={40} titre="Semaine 39" sousTitre="déjà remplie" />
        <Boite x={228} y={140} w={150} h={40} titre="Semaine 40" sousTitre="déjà remplie" />
        <Fleche x1={384} x2={436} y={100} />
        <Boite x={442} y={62} w={146} h={76} titre="Vous corrigez" sousTitre="les exceptions" />
    </Cadre>
);

/** Menus & Convives : du fichier du client jusqu'à la fiche de la cuisine. */
const ConvivesDuFichierALaCuisine = () => (
    <Cadre>
        <Boite x={10} y={72} w={122} h={56} titre="Le client" sousTitre="nom, prénom, email" />
        <Fleche x1={138} x2={180} y={100} />
        <Boite x={186} y={72} w={122} h={56} titre="Le convive" sousTitre="choisit son plat" accentuee />
        <Fleche x1={314} x2={356} y={100} />
        <Boite x={362} y={72} w={112} h={56} titre="Vous" sousTitre="suivez les réponses" />
        <Fleche x1={480} x2={522} y={100} />
        <Boite x={528} y={72} w={62} h={56} titre="Cuisine" />
        <text x={247} y={44} textAnchor="middle" fontSize={11} fill={ESTOMPE}>
            allergies et régimes déclarés ici
        </text>
        <line x1={247} y1={52} x2={247} y2={68} stroke={BORDURE} strokeWidth={1.5} strokeDasharray="3 3" />
        <text x={300} y={168} textAnchor="middle" fontSize={11} fill={ESTOMPE}>
            Vous ne demandez jamais au client ce que mange chacun de ses invités.
        </text>
    </Cadre>
);

/** Menus & Convives : d'un ingrédient détaillé jusqu'à l'assiette adaptée. Une seule ligne, quatre
 *  étapes : le premier jet renvoyait la dernière boîte sur un second niveau, et la flèche qui l'y menait
 *  partait dans le vide à l'écran. */
const ConvivesAllergieAdaptation = () => (
    <Cadre>
        <text x={73} y={30} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ESTOMPE}>VOTRE CARTE</text>
        <text x={232} y={30} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ESTOMPE}>L'APPLICATION</text>
        <text x={397} y={30} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ESTOMPE}>LE CONVIVE</text>
        <text x={548} y={30} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ESTOMPE}>LA CUISINE</text>

        <Boite x={14} y={48} w={118} h={54} titre="Crème fraîche" sousTitre="possible de retirer" />
        <Fleche x1={138} x2={174} y={75} />
        <Boite x={180} y={48} w={104} h={54} titre="Lactose" sousTitre="déduit tout seul" accentuee />
        <Fleche x1={290} x2={326} y={75} />
        <Boite x={332} y={48} w={130} h={54} titre="« j'y suis allergique »" sousTitre="il coche le retrait" />
        <Fleche x1={468} x2={504} y={75} />
        <Boite x={510} y={48} w={76} h={54} titre="sans crème" sousTitre="sur la feuille" />

        <text x={300} y={140} textAnchor="middle" fontSize={11} fill={ESTOMPE}>
            Un ingrédient qu'on ne peut pas retirer ne rend pas le plat adaptable :
        </text>
        <text x={300} y={157} textAnchor="middle" fontSize={11} fill={ESTOMPE}>
            il est alors bloqué pour ce convive, qui en choisit un autre.
        </text>
    </Cadre>
);

/** Menus & Convives : les trois gestes que l'organisateur peut faire à votre place.
 *
 *  Pas de boîte « Vous gardez la main » au bout d'une flèche : à l'écran, elle se lisait comme une
 *  quatrième étape du parcours de l'organisateur, alors que c'est le contraire — ce qu'il ne fait pas.
 *  C'est dit en toutes lettres sous le dessin. */
const ConvivesOrganisateur = () => (
    <Cadre>
        <Boite x={60} y={62} w={170} h={74} titre="L'organisateur" sousTitre="lien + code provisoires" accentuee />
        <Fleche x1={236} x2={282} y={99} />
        <Boite x={290} y={32} w={250} h={40} titre="Importer la liste" />
        <Boite x={290} y={79} w={250} h={40} titre="Envoyer les invitations" />
        <Boite x={290} y={126} w={250} h={40} titre="Relancer les silencieux" />
        <text x={300} y={190} textAnchor="middle" fontSize={11} fill={ESTOMPE}>
            Il ne voit ni tarif, ni contrainte alimentaire nominative — et rien d'autre ne lui est ouvert.
        </text>
    </Cadre>
);

/** Mode terrain : ce qui se passe quand un convive scanne le QR de sa table.
 *
 *  Les abscisses sont calculées pour que chaque flèche ait sa place : au premier jet, la dernière boîte
 *  commençait AVANT la fin de la flèche qui la désignait, et les deux dernières se touchaient. */
const TerrainDuQrAuPasse = () => (
    <Cadre>
        <Boite x={10} y={58} w={120} h={58} titre="Le QR de la table" sousTitre="un par table" accentuee />
        <Fleche x1={136} x2={168} y={87} />
        <Boite x={174} y={58} w={126} h={58} titre="Il s'installe" sousTitre="son adresse, c'est tout" />
        <Fleche x1={306} x2={338} y={87} />
        <Boite x={344} y={58} w={108} h={58} titre="La cuisine" sousTitre="sait quoi sortir" />
        <Fleche x1={458} x2={490} y={87} />
        <Boite x={496} y={58} w={94} h={58} titre="Le passe" sousTitre="annonce" />
    </Cadre>
);

/** Mode terrain : trois façons de regarder le même service. */
const TerrainTroisVues = () => (
    <Cadre>
        <Boite x={26} y={62} w={158} h={74} titre="Le service de ce soir" sousTitre="une seule source" accentuee />
        <Fleche x1={190} x2={236} y={99} />
        <Boite x={244} y={28} w={300} h={42} titre="Plan de table" sousTitre="qui est assis où" />
        <Boite x={244} y={78} w={300} h={42} titre="Allergies" sousTitre="ce qu'on annonce au passe" />
        <Boite x={244} y={128} w={300} h={42} titre="Entrée · Plat · Dessert" sousTitre="ce qu'on sort, table par table" />
    </Cadre>
);

/** Planning : les deux façons de poser une absence, selon sa durée. */
const PlanningAbsenceCourteOuLongue = () => (
    <Cadre>
        <text x={152} y={28} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ESTOMPE}>UN JOUR, OU UNE PARTIE DE JOUR</text>
        <Boite x={20} y={46} w={126} h={52} titre="La cellule" sousTitre="du jour concerné" />
        <Fleche x1={152} x2={188} y={72} />
        <Boite x={194} y={46} w={116} h={52} titre="Le code" sousTitre="au lieu de l'horaire" accentuee />
        <text x={152} y={128} textAnchor="middle" fontSize={11} fill={ESTOMPE}>Un code « journée entière »</text>
        <text x={152} y={144} textAnchor="middle" fontSize={11} fill={ESTOMPE}>remplace tout le jour.</text>

        <line x1={330} y1={34} x2={330} y2={170} stroke={BORDURE} strokeWidth={1.5} strokeDasharray="4 4" />

        <text x={462} y={28} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={ESTOMPE}>PLUSIEURS SEMAINES</text>
        <Boite x={352} y={46} w={116} h={52} titre="« Ajouter »" sousTitre="Absence longue" />
        <Fleche x1={474} x2={508} y={72} />
        <Boite x={500} y={46} w={88} h={52} titre="Début" sousTitre="et fin" accentuee />
        <text x={470} y={128} textAnchor="middle" fontSize={11} fill={ESTOMPE}>Elle se pose d'un coup sur</text>
        <text x={470} y={144} textAnchor="middle" fontSize={11} fill={ESTOMPE}>toutes les semaines concernées.</text>
    </Cadre>
);

export interface SchemaDuGuide {
    /** Ce qu'Emeni lit dans la liste à vignettes. */
    label: string;
    Composant: React.ComponentType;
}

export const SCHEMAS: Record<string, SchemaDuGuide> = {
    'planning-semaine-par-defaut': { label: 'Planning — la semaine se remplit toute seule', Composant: PlanningSemaineParDefaut },
    'convives-du-fichier-a-la-cuisine': { label: 'Convives — du fichier du client à la cuisine', Composant: ConvivesDuFichierALaCuisine },
    'convives-allergie-adaptation': { label: 'Convives — d’un ingrédient à l’assiette adaptée', Composant: ConvivesAllergieAdaptation },
    'convives-organisateur': { label: 'Convives — les trois gestes de l’organisateur', Composant: ConvivesOrganisateur },
    'terrain-du-qr-au-passe': { label: 'Mode terrain — du QR de la table au passe', Composant: TerrainDuQrAuPasse },
    'terrain-trois-vues': { label: 'Mode terrain — les trois façons de regarder le service', Composant: TerrainTroisVues },
    'planning-absence-courte-ou-longue': { label: 'Planning — absence d’un jour ou de plusieurs semaines', Composant: PlanningAbsenceCourteOuLongue },
};
